import { useState, useEffect } from 'react';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import Datetime from 'react-datetime';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Check from '@material-ui/icons/Check';
import Checkbox from '@material-ui/core/Checkbox';
import FormLabel from '@material-ui/core/FormLabel';
import { makeStyles } from '@material-ui/styles';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Button from 'components/CustomButtons/Button.jsx';
import GridItem from 'components/Grid/GridItem';
import SelectInput from 'components/FormSelect/SelectInput';
import CustomInput from 'components/CustomInput/CustomInput';
import GridContainer from 'components/Grid/GridContainer';
import FormSelectSingle from 'components/FormSelect/FormSelectSingle';
import FormTextInputNoGrid from 'components/FormTextInput/FormTextInputNoGrid';
import useDebounce from 'hooks/common/useDebounce';
import { yesNoOptions } from 'utils/yesNoOptions';
import { fetchUserAddresses } from 'actions/Addresses';
import { get, post, put } from 'helpers/apiHelpers';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import { DialogLoader } from 'components/DialogLoader';
import { toast } from 'react-toastify';
import classNames from 'classnames';

import EmojiButton from '../../../../components/EmojiButton/EmojiButton';
import defaultState from './defaultState';

const useStyles = makeStyles({
  ...buttonsStyle,
  ...extendedFormsStyle,
  gridItem: {
    marginBottom: '0',
  },

  gridItemSpacingTop: {
    marginTop: '11px',
  },
  gridItemNegativeSpacing: {
    marginTop: '-10px',
  },
  gridItemSmallSpacing: {
    marginTop: '5px',
  },
  emojiButtonWrapper: {
    marginLeft: '10px',
    position: 'relative',
  },
  addIconButton: {
    marginLeft: '10px',
    position: 'relative',
  },

  formLabel: {
    marginBottom: '11px',
    ...extendedFormsStyle.labelHorizontal,
  },
});

const AddressForm = ({
  match,
  closeModal,
  editAddress,
  reRenderModal,
  // eslint-disable-next-line no-shadow -- FIXME
  fetchUserAddresses,
  useAddressesWithLessFields,
  selectedRegionForValidations,
  drivers,
  ...restProps
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [assignedDriver, setAssignedDriver] = useState([]);
  const [showIntercomCodeEmoji, setShowIntercomCodeEmoji] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [cities, setCities] = useState([]);
  const [streets, setStreets] = useState([]);
  const [deliveryHours, setDeliveryHours] = useState([]);
  const [buildingNumbers, setBuildingNumbers] = useState([]);

  const [state, setState] = useState({
    ...defaultState,
  });

  const isEditing = editAddress.length !== 0;
  const userId = restProps?.userId ?? match.params.id;
  const debouncedCity = useDebounce(state.city);
  const debouncedStreet = useDebounce(state.street);
  const debouncedPostCode = useDebounce(state.postCode);
  const debouncedBuildNumber = useDebounce(state.buildNumber);

  const isValidAddress =
    !isEmpty(state.city) &&
    !isEmpty(state.street) &&
    !isEmpty(state.buildNumber) &&
    !isEmpty(debouncedCity) &&
    !isEmpty(debouncedStreet) &&
    !isEmpty(debouncedPostCode) &&
    !isEmpty(debouncedBuildNumber);

  useEffect(() => {
    if (isEditing) {
      // eslint-disable-next-line no-use-before-define -- FIXME
      loadAddressToEdit();
    } else {
      setIsInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, []);

  useEffect(() => {
    if (isInitialized && isValidAddress) {
      // eslint-disable-next-line no-use-before-define -- FIXME
      fetchDeliveryHours({
        city: debouncedCity,
        street: debouncedStreet,
        postCode: debouncedPostCode,
        buildNumber: debouncedBuildNumber,
      });
    } else if (isInitialized && !isValidAddress) {
      setDeliveryHours([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [debouncedPostCode, debouncedCity, debouncedStreet, debouncedBuildNumber]);

  useEffect(() => {
    // eslint-disable-next-line no-use-before-define -- FIXME
    valdiateDeliveryHour();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [deliveryHours]);

  const loadAddressToEdit = async () => {
    const address = editAddress[0];

    // eslint-disable-next-line no-unsafe-optional-chaining -- FIXME
    const [addressLine1 = '', addressLine2 = ''] = address?.street?.split('; ');

    try {
      // eslint-disable-next-line no-use-before-define -- FIXME
      const citiesData = await fetchCities(address?.postCode);
      // eslint-disable-next-line no-use-before-define -- FIXME
      const streetsData = await fetchStreets({
        postCode: address?.postCode,
        city: address?.city,
      });
      // eslint-disable-next-line no-use-before-define -- FIXME
      const buildingNumbersData = await fetchBuildingNumbers({
        postCode: address?.postCode,
        city: address?.city,
        street: address?.street,
      });
      // eslint-disable-next-line no-use-before-define -- FIXME
      await fetchDeliveryHours({
        city: address?.city,
        street: address?.street,
        postCode: address?.postCode,
        buildNumber: address?.buildNumber,
      });
      // eslint-disable-next-line no-use-before-define -- FIXME
      setCities(formatCities(citiesData));
      // eslint-disable-next-line no-use-before-define -- FIXME
      setStreets(formatStreets(streetsData));
      // eslint-disable-next-line no-use-before-define -- FIXME
      setBuildingNumbers(formatBuildingNumbers(buildingNumbersData));
    } catch (error) {
      // eslint-disable-next-line no-console -- FIXME
      console.error(error);
    }

    if (address?.zone) {
      try {
        const res = await get(address.zone['@id'], null, {
          params: {
            properties: ['drivers', 'deliveryType'],
          },
        });

        let driver;
        const addressDrivers = res.drivers;

        if (address.driver) {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- FIXME
          !addressDrivers.some(
            // eslint-disable-next-line no-shadow -- FIXME
            driver => driver['@id'] === address.driver['@id']
          ) && addressDrivers.push(address.driver);
          driver = addressDrivers.find(
            // eslint-disable-next-line no-shadow -- FIXME
            driver => driver['@id'] === address.driver['@id']
          )['@id'];
        } else {
          driver = null;
        }

        setAssignedDriver(addressDrivers);
        setState({
          ...state,
          id: address.id,
          city: address.city,
          zone: address.zone ? address.zone.name : '',
          gate: address.gate,
          floor: address.floor,
          street: address.street,
          driver,
          default: address.isDefault === false ? 1 : 2,
          comment: address.comment,
          hourFrom: moment(address?.selectedDeliveryHour?.hourFrom).format(
            'HH:mm'
          ),
          hourTo: moment(address?.selectedDeliveryHour?.hourTo).format('HH:mm'),
          postCode: address.postCode,
          keyToGate: address.keyToGate,
          buildNumber: address.buildNumber,
          placeNumber: address.placeNumber,
          addressLine1,
          addressLine2,
          deliveryType: res?.deliveryType ?? null,
          // eslint-disable-next-line no-use-before-define -- FIXME
          selectedHour: formatSelectedHour(address),
          keyToIntercom: address.keyToIntercom,
          postCodeIsValid: true,
          customDeliveryHours: address.customDeliveryHours,
        });
      } catch (error) {
        // eslint-disable-next-line no-console -- FIXME
        console.error(error);
      }
    } else {
      setState({
        ...state,
        default: address.isDefault === false ? 1 : 2,
        id: address.id,
        city: address.city,
        customDeliveryHours: address.customDeliveryHours,
        postCode: address.postCode,
        postCodeIsValid: true,
        street: address.street,
        addressLine1,
        addressLine2,
        deliveryType: null,
        buildNumber: address.buildNumber,
        placeNumber: address.placeNumber,
        floor: address.floor,
        gate: address.gate,
        keyToGate: address.keyToGate,
        keyToIntercom: address.keyToIntercom,
        comment: address.comment,
      });
    }

    setTimeout(() => {
      setIsInitialized(true);
    }, 2000);
  };

  const formatCities = citiesData =>
    citiesData?.allowCustom
      ? []
      : (citiesData?.cities?.['hydra:member'] || []).map(currentCity => ({
          '@id': currentCity,
          label: currentCity,
        }));

  const formatStreets = streetsData =>
    streetsData?.allowCustom
      ? []
      : (streetsData?.streets?.['hydra:member'] || []).map(currentStreet => ({
          '@id': currentStreet,
          label: currentStreet,
        }));

  const formatBuildingNumbers = buildingNumbersData =>
    buildingNumbersData?.allowCustom
      ? []
      : (buildingNumbersData?.streets?.['hydra:member'] || []).map(
          currentBuildinNumber => ({
            '@id': currentBuildinNumber,
            label: currentBuildinNumber,
          })
        );

  const formatSelectedHour = address => ({
    '@id': address?.selectedDeliveryHour?.zoneDeliveryHours?.['@id'],
    hourFrom: address?.selectedDeliveryHour?.hourFrom,
    hourTo: address?.selectedDeliveryHour?.hourTo,
    label: `${t('common.from')} ${moment(
      address?.selectedDeliveryHour?.hourFrom
    ).format('HH:mm')} ${t('common.to')} ${moment(
      address?.selectedDeliveryHour?.hourTo
    ).format('HH:mm')}`,
  });

  const handleChange = (event, obj) => {
    const newState = {
      ...state,
      [event.target.name]: obj || event.target.value,
    };

    setState(newState);
  };

  const handleChangeAddSymbol = emoji => {
    setState(prev => ({
      ...prev,
      keyToIntercom: emoji,
    }));
  };

  const handlePostCodeInput = async event => {
    const postCode = event.target.value;
    const regexp = selectedRegionForValidations?.postCodeRegexp;
    const regexpTest = new RegExp(`${regexp}$`);
    const postCodeIsValid = regexp === null || regexpTest.test(postCode);
    // eslint-disable-next-line no-useless-assignment -- FIXME
    let newCities = [];

    const newState = {
      ...state,
      postCode,
      postCodeIsValid,
      postCodeHelpText: '',
    };

    setState(newState);

    if (postCodeIsValid) {
      try {
        // eslint-disable-next-line no-use-before-define -- FIXME
        const citiesData = await fetchCities(postCode);
        newCities = formatCities(citiesData);

        const isCurrentCityValid =
          !isEmpty(state.city) &&
          newCities.map(({ label }) => label).includes(state.city);

        if (!isCurrentCityValid) {
          setState({
            ...newState,
            city: '',
          });
        }
        setCities(newCities);
      } catch ({ response }) {
        const isPostCodeInvalid = (response?.data?.violations ?? []).some(
          ({ propertyPath }) => propertyPath === 'postCode'
        );

        if (isPostCodeInvalid) {
          setState({ ...newState, postCodeIsValid: false });
        }
      }
    }
  };

  const handleCitySelect = async event => {
    const { postCode } = state;

    // eslint-disable-next-line no-use-before-define -- FIXME
    const streetsData = await fetchStreets({
      postCode,
      city: event.target.value,
    });
    const newStreets = formatStreets(streetsData);

    setState(() => ({
      ...state,
      city: event.target.value,
      street:
        newStreets.map(({ label }) => label).includes(state.street) ||
        isEmpty(newStreets)
          ? state.street
          : '',
    }));
    setStreets(newStreets);
  };

  const handleStreetSelect = async event => {
    const { postCode, city } = state;
    // eslint-disable-next-line no-use-before-define -- FIXME
    const buildingNumbersData = await fetchBuildingNumbers({
      postCode,
      city,
      street: event.target.value,
    });
    const newBuildingNumbers = formatBuildingNumbers(buildingNumbersData);

    setState(() => ({
      ...state,
      street: event.target.value,
      buildNumber:
        newBuildingNumbers
          .map(({ label }) => label)
          .includes(state.buildNumber) || isEmpty(newBuildingNumbers)
          ? state.buildNumber
          : '',
    }));
    setBuildingNumbers(newBuildingNumbers);
  };

  // eslint-disable-next-line require-await -- FIXME
  const handleBuildingNumberSelect = async event => {
    setState(() => ({
      ...state,
      buildNumber: event.target.value,
    }));
  };

  const fetchCities = postCode =>
    get(`/frontend/zone-post-codes/cities?postCode=${postCode}`);

  const fetchStreets = ({ postCode, city }) =>
    get(
      `/frontend/zone-post-codes/cities/streets?postCode=${postCode}&city=${city}`
    );

  const fetchBuildingNumbers = ({ postCode, city, street }) =>
    get(
      `/frontend/zone-post-codes/cities/streets/buildings?postCode=${postCode}&city=${city}&street=${street}`
    );

  const fetchDeliveryHours = async ({
    city,
    street,
    postCode,
    buildNumber,
  }) => {
    const deliveryHoursData = await get(
      `/frontend/delivery-hours?postCode=${postCode}&city=${city}&street=${street}&building=${buildNumber}`
    );

    const hours = (deliveryHoursData?.hours ?? []).map(hour => {
      const formattedHourFrom = hour?.hourFrom
        ? // eslint-disable-next-line new-cap -- FIXME
          new moment(hour.hourFrom).format('HH:mm')
        : null;
      const formattedHourTo = hour?.hourTo
        ? // eslint-disable-next-line new-cap -- FIXME
          new moment(hour.hourTo).format('HH:mm')
        : null;

      return {
        '@id': hour['@id'],
        hourFrom: hour?.hourFrom,
        hourTo: hour?.hourTo,
        label: [
          hour?.hourFrom &&
            [t('$*common.from', '$$od'), formattedHourFrom].join(' '),
          hour?.hourTo && [t('$*common.to', '$$do'), formattedHourTo].join(' '),
        ].join(' '),
      };
    });
    setDeliveryHours(hours ?? []);
  };

  const valdiateDeliveryHour = () => {
    const isCurrentDeliveryHourAvaliable = deliveryHours.some(
      ({ '@id': iri }) => iri === state.selectedHour?.['@id']
    );

    if (
      isEmpty(deliveryHours) ||
      (!isEmpty(deliveryHours) && !isCurrentDeliveryHourAvaliable)
    ) {
      setState({ ...state, selectedHour: null });
    }
  };

  const validateForm = () => {
    const { city, street, postCode, buildNumber, addressLine1 } = state;

    return useAddressesWithLessFields
      ? addressLine1 && postCode && city
      : buildNumber && street && postCode && city;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, consistent-return -- FIXME
  const handleSubmit = ev => {
    setIsLoading(true);

    if (!state.postCodeIsValid || state.postCode.length === 0) {
      setIsLoading(false);
      return toast.error(t('form.postCodeIncorrect'));
    }
    if (!validateForm()) {
      setIsLoading(false);
      return toast.error(t('clients.fillAllFields'));
    }

    const data = {
      default: state.default !== 1,
      user: `/clients/${userId}`,
      postCode: state.postCode,
      city: state.city,
      ...(useAddressesWithLessFields
        ? {
            street: `${state.addressLine1}; ${state.addressLine2}`,
            buildNumber: '0',
          }
        : { street: state.street, buildNumber: state.buildNumber }),
      placeNumber: state.placeNumber,
      gate: state.gate,
      // eslint-disable-next-line radix -- FIXME
      floor: parseInt(state.floor),
      keyToGate: state.keyToGate,
      keyToIntercom: state.keyToIntercom,
      comment: state.comment,
      deliveryType: state.deliveryType?.['@id'] ?? null,
      customDeliveryHours: state.customDeliveryHours,
      ...(state.customDeliveryHours
        ? {
            deliveryHourFrom: state.hourFrom,
            deliveryHourTo: state.hourTo,
          }
        : {
            deliveryHourFrom: state.selectedHour?.hourFrom,
            deliveryHourTo: state.selectedHour?.hourTo,
          }),
      driver: state.driver || null,
    };

    const action = isEditing
      ? put(`/addresses/${state.id}`, data)
      : post('/addresses', data);

    // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
    action.then(
      async response => {
        await fetchUserAddresses(userId);
        reRenderModal(response.id);
        setIsLoading(false);
        if (typeof closeModal !== 'undefined') {
          closeModal();
        }
        return toast.success(
          isEditing
            ? t('clients.addressChangesSaved')
            : t('clients.addressCreated')
        );
      },
      // eslint-disable-next-line promise/prefer-await-to-callbacks -- FIXME
      error => {
        if (
          error.response.data.violations &&
          error.response.data.violations[0].propertyPath === 'postCode'
        ) {
          setIsLoading(false);
          return toast.error(error.response.data.violations[0].message);
        }
        setIsLoading(false);
        return toast.error(
          `${t('clients.smthWentWrong')} ${t('clients.checkForm')}`
        );
      }
    );
  };

  const handleToggle = () => {
    setState({ ...state, customDeliveryHours: !state.customDeliveryHours });
  };

  return (
    <>
      {isEditing && (
        <DialogLoader loaderState={!isInitialized} text={t('common.loader')} />
      )}

      <form>
        <GridContainer>
          <GridItem sm={12}>
            <GridContainer>
              {/* POST CODE */}
              <GridItem sm={6} className={classes.gridItem}>
                <FormLabel className={classes.formLabel}>
                  {t('clients.postCode')} *
                </FormLabel>
                <FormTextInputNoGrid
                  classes={classes}
                  customInput={FormTextInputNoGrid}
                  value={state.postCode}
                  success={state.postCodeIsValid}
                  error={!state.postCodeIsValid}
                  helpText={state.postCodeHelpText}
                  onChange={e => handlePostCodeInput(e)}
                  name="postCode"
                />
              </GridItem>
              {/* CITY */}
              <GridItem sm={6}>
                {isEmpty(cities) ? (
                  <>
                    <FormLabel className={classes.formLabel}>
                      {t('common.address.city')} *
                    </FormLabel>
                    <CustomInput
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        name: 'city',
                        value: state.city,
                        onChange: ev => handleChange(ev),
                      }}
                      disabled={!state.postCodeIsValid}
                    />
                  </>
                ) : (
                  <SelectInput
                    classes={classes}
                    label={`${t('common.address.city')}*`}
                    options={cities}
                    value={state.city}
                    mapBy="label"
                    trackBy="@id"
                    name="city"
                    handleChange={handleCitySelect}
                    id="city"
                    disabled={isEmpty(cities)}
                  />
                )}
              </GridItem>
              {/* SHORT ADDRESS FORM INPUTS */}
              {useAddressesWithLessFields && (
                <>
                  <GridItem sm={6} className={classes.gridItemSmallSpacing}>
                    <FormLabel className={classes.formLabel}>
                      {t('clients.addressLine1')} *
                    </FormLabel>
                    <CustomInput
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        name: 'addressLine1',
                        value: state.addressLine1,
                        onChange: ev => handleChange(ev),
                      }}
                    />
                  </GridItem>
                  <GridItem sm={6} className={classes.gridItemSmallSpacing}>
                    <FormLabel className={classes.formLabel}>
                      {t('clients.addressLine2')}
                    </FormLabel>
                    <CustomInput
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        name: 'addressLine2',
                        value: state.addressLine2,
                        onChange: ev => handleChange(ev),
                      }}
                    />
                  </GridItem>
                </>
              )}

              {!useAddressesWithLessFields && (
                <>
                  {/* STREET */}
                  <GridItem sm={4} className={classes.gridItemSmallSpacing}>
                    {isEmpty(cities) || isEmpty(streets) ? (
                      <>
                        <FormLabel className={classes.formLabel}>
                          {t('common.address.street')} *
                        </FormLabel>
                        <CustomInput
                          formControlProps={{ fullWidth: true }}
                          inputProps={{
                            name: 'street',
                            value: state.street,
                            onChange: ev => handleChange(ev),
                          }}
                          disabled={isEmpty(state.city)}
                        />
                      </>
                    ) : (
                      <SelectInput
                        classes={classes}
                        label={`${t('common.address.street')} *`}
                        options={streets}
                        value={state.street}
                        mapBy="label"
                        trackBy="@id"
                        name="street"
                        handleChange={handleStreetSelect}
                        id="street"
                        disabled={isEmpty(streets)}
                      />
                    )}
                  </GridItem>
                  {/* BUILDING NUMBER */}
                  <GridItem sm={4} className={classes.gridItemSmallSpacing}>
                    {isEmpty(cities) ||
                    isEmpty(streets) ||
                    isEmpty(buildingNumbers) ? (
                      <>
                        <FormLabel className={classes.formLabel}>
                          {t('common.address.buildingNo')} *
                        </FormLabel>
                        <CustomInput
                          formControlProps={{ fullWidth: true }}
                          inputProps={{
                            name: 'buildNumber',
                            maxLength: 16,
                            value: state.buildNumber,
                            onChange: ev => handleChange(ev),
                          }}
                          disabled={isEmpty(state.street)}
                        />
                      </>
                    ) : (
                      <SelectInput
                        classes={classes}
                        label={`${t('common.address.buildingNo')} *`}
                        options={buildingNumbers}
                        value={state.buildNumber}
                        mapBy="label"
                        trackBy="@id"
                        name="buildNumber"
                        handleChange={handleBuildingNumberSelect}
                        id="buildNumber"
                        disabled={isEmpty(buildingNumbers)}
                      />
                    )}
                  </GridItem>
                  {/* DOOR NUMBER */}
                  <GridItem sm={4} className={classes.gridItemSmallSpacing}>
                    <FormLabel className={classes.formLabel}>
                      {t('common.address.localNo')}
                    </FormLabel>
                    <CustomInput
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        name: 'placeNumber',
                        maxLength: 16,
                        value: state.placeNumber,
                        onChange: ev => handleChange(ev),
                      }}
                    />
                  </GridItem>
                  {/* GATE */}
                  <GridItem sm={4} className={classes.gridItemSmallSpacing}>
                    <FormLabel className={classes.formLabel}>
                      {t('clients.gate')}
                    </FormLabel>
                    <CustomInput
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        name: 'gate',
                        maxLength: 16,
                        value: state.gate,
                        onChange: ev => handleChange(ev),
                      }}
                    />
                  </GridItem>
                  {/* FLOOR */}
                  <GridItem sm={4} className={classes.gridItemSpacingTop}>
                    <FormLabel className={classes.formLabel}>
                      {t('clients.floor')}
                    </FormLabel>
                    <CustomInput
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        name: 'floor',
                        type: 'number',
                        value: state.floor,
                        onChange: ev => handleChange(ev),
                      }}
                    />
                  </GridItem>
                  {/* INTERCOM CODE */}
                  <GridItem sm={4} className={classNames(classes.gridItem)}>
                    <FormLabel className={classes.formLabel}>
                      {t('clients.callCode')}
                    </FormLabel>
                    <Button
                      className={classes.addIconButton}
                      size="sm"
                      color="info"
                      onClick={() => setShowIntercomCodeEmoji(true)}
                    >
                      {t('common.address.addIconButton')}
                    </Button>

                    <CustomInput
                      formControlProps={{ fullWidth: true }}
                      maxLength={32}
                      inputProps={{
                        name: 'keyToIntercom',
                        value: state.keyToIntercom,
                        onChange: ev => handleChange(ev),
                      }}
                    />
                    {showIntercomCodeEmoji && (
                      <div className={classes.emojiButtonWrapper}>
                        <EmojiButton
                          closeEmojiModal={setShowIntercomCodeEmoji}
                          handleChange={handleChangeAddSymbol}
                          prevState={state}
                        />
                      </div>
                    )}
                  </GridItem>
                </>
              )}

              <GridItem
                sm={6}
                className={classNames(
                  classes.gridItemSmallSpacing,
                  classes.gridItem
                )}
              >
                <FormSelectSingle
                  className={classes.gridItemSmallSpacing}
                  label={t('clients.defaultAddress')}
                  classes={classes}
                  options={yesNoOptions({ t }).map(option => {
                    // eslint-disable-next-line no-plusplus -- FIXME
                    option.value++;
                    return option;
                  })}
                  value={state.default}
                  mapBy="name"
                  trackBy="value"
                  name="default"
                  handleChange={ev => handleChange(ev)}
                  id="default"
                />
              </GridItem>
              {/* ZONE */}
              <GridItem sm={6} className={classes.gridItemSpacingTop}>
                <FormLabel
                  className={classNames(
                    classes.labelHorizontal,
                    classes.gridItemSmallSpacing
                  )}
                >
                  {t('clients.zone')}
                </FormLabel>
                <CustomInput
                  formControlProps={{ fullWidth: true }}
                  // eslint-disable-next-line no-negated-condition -- FIXME
                  helpText={state.zone !== '' ? '' : t('clients.thisFieldAuto')}
                  inputProps={{
                    disabled: true,
                    value: state.zone,
                  }}
                />
              </GridItem>
              {/* ADDITIONAL NOTES */}
              <GridItem
                sm={12}
                className={classNames(
                  classes.gridItemNegativeSpacing,
                  classes.gridItem
                )}
              >
                <FormLabel className={classes.formLabel}>
                  {t('common.address.notes')}
                </FormLabel>
                <CustomInput
                  formControlProps={{ fullWidth: true }}
                  maxLength={500}
                  inputProps={{
                    multiline: true,
                    name: 'comment',
                    value: state.comment,
                    onChange: ev => handleChange(ev),
                  }}
                />
              </GridItem>
              {/* DELIVERY TYPE */}
              <GridItem sm={4} className={classes.gridItemSmallSpacing}>
                <FormLabel className={classes.formLabel}>
                  {t('clients.type')}
                </FormLabel>
                <CustomInput
                  formControlProps={{ fullWidth: true }}
                  helpText={
                    // eslint-disable-next-line no-negated-condition -- FIXME
                    state.deliveryType !== '' ? '' : t('clients.thisFieldAuto')
                  }
                  inputProps={{
                    disabled: true,
                    value: state.deliveryType?.value,
                  }}
                />
              </GridItem>
              {/* STANDARD DELIVERY HOURS */}
              {!state.customDeliveryHours && (
                <GridItem sm={4} className={classes.gridItemSmallSpacing}>
                  <SelectInput
                    classes={classes}
                    label={`${t('common.address.deliveryHour')}`}
                    options={deliveryHours}
                    value={state.selectedHour}
                    mapBy="label"
                    trackBy="@id"
                    name="selectedHour"
                    handleChange={(ev, obj) => handleChange(ev, obj)}
                    id="selectedHour"
                    disabled={isEmpty(deliveryHours)}
                  />
                  <p>{t('$*users.address.editing.deliveryHours')}</p>
                </GridItem>
              )}
              {/* DRIVER */}
              <GridItem sm={4} className={classes.gridItemSmallSpacing}>
                <div
                  onClick={() =>
                    !isEditing && toast.info(t('clients.driverAuto'))
                  }
                >
                  <SelectInput
                    classes={classes}
                    label={t('clients.assignedDriver')}
                    options={assignedDriver}
                    value={state.driver}
                    mapBy="name"
                    trackBy="@id"
                    name="driver"
                    handleChange={ev => handleChange(ev)}
                    disabled={!isEditing}
                    id="driver"
                  />
                </div>
              </GridItem>
              {/* CUSTOM DELIVERY HOURS */}
              <GridItem sm={12} className={classes.gridItemSmallSpacing}>
                <GridContainer>
                  <GridItem sm={4} className={classes.gridItemSmallSpacing}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="customDeliveryHours"
                          onClick={handleToggle}
                          checked={state.customDeliveryHours}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                            root: classes.checkRoot,
                          }}
                        />
                      }
                      classes={{
                        label: classes.label,
                      }}
                      label={t('form.customDeliveryHours')}
                    />
                  </GridItem>
                  {state.customDeliveryHours && (
                    <GridItem sm={4} className={classes.gridItemSmallSpacing}>
                      <div>
                        <FormLabel
                          className={classNames(
                            classes.labelHorizontal,
                            classes.gridItemSmallSpacing
                          )}
                        >
                          {`${t('comments.from')} *`}
                        </FormLabel>
                        <Datetime
                          dateFormat={false}
                          value={state.hourFrom}
                          onChange={ev =>
                            setState({ ...state, hourFrom: ev.format('HH:mm') })
                          }
                        />
                      </div>
                    </GridItem>
                  )}
                  {state.customDeliveryHours && (
                    <GridItem sm={4} className={classes.gridItemSmallSpacing}>
                      <div>
                        <FormLabel
                          className={classNames(
                            classes.labelHorizontal,
                            classes.gridItemSmallSpacing
                          )}
                        >
                          {`${t('comments.to')} *`}
                        </FormLabel>
                        <Datetime
                          dateFormat={false}
                          value={state.hourTo}
                          onChange={ev =>
                            setState({ ...state, hourTo: ev.format('HH:mm') })
                          }
                        />
                      </div>
                    </GridItem>
                  )}
                </GridContainer>
              </GridItem>
            </GridContainer>
          </GridItem>
        </GridContainer>
        {/* BUTTONS */}
        <GridContainer justify="center">
          <GridItem className={classes.gridItemSmallSpacing}>
            <Button
              className={classes.marginRight}
              onClick={() => closeModal()}
            >
              {t('common.shared.cancel')}
            </Button>
            <Button
              color="success"
              className={classes.marginLeft}
              disabled={isLoading}
              onClick={handleSubmit}
            >
              {t('common.shared.save')}
            </Button>
          </GridItem>
        </GridContainer>
      </form>
    </>
  );
};

const mapStateToProps = state => ({
  drivers: state.Drivers.drivers,
  selectedRegionForValidations: state.Brands.selectedRegionForValidations,
  useAddressesWithLessFields:
    state.Brands.modules?.ConfigClientPanel?.useAddressesWithLessFields ??
    false,
});

const mapDispatchToProps = dispatch => ({
  fetchUserAddresses: id => dispatch(fetchUserAddresses(id)),
});

const enhance = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(AddressForm);
